<template>
    <b-row>
        <b-col
            md="5"
            class="document-compliance-preview-column"
        >
            <DocumentPreviewSection :file-reference-id="fileReferenceId" />
        </b-col>

        <b-col md="7">
            <b-alert
                v-if="violations.length"
                variant="danger"
                show
            >
                <div><strong>{{ $t('Component(ReviewProcess):validation-failed') }}</strong></div>
                <ul class="mb-0 pl-1">
                    <li
                        v-for="(v, index) in violations"
                        :key="index"
                    >
                        {{ v.path }}: {{ v.message }}
                    </li>
                </ul>
            </b-alert>

            <DocumentComplianceEditor
                v-model="draftReviewedPayload"
                :supplier-id="supplierId"
                :review-capabilities="reviewCapabilities"
            />

            <ReviewItemActionsSection
                :status="status"
                :comment="comment"
                :saving="saving"
                :status-options="statusOptions"
                :show-cancel="showCancel"
                :show-save="showActions"
                :cancel-label="cancelLabel"
                :save-label="saveLabel"
                @update:status="status = $event"
                @update:comment="comment = $event"
                @cancel="$emit('cancel')"
                @save="save"
            />
        </b-col>
    </b-row>
</template>

<script>
import {
    BAlert,
    BCol,
    BRow,
} from 'bootstrap-vue'
import { ResearchReviewApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import DocumentComplianceEditor from './DocumentComplianceEditor.vue'
import ReviewItemActionsSection from './sections/ReviewItemActionsSection.vue'
import DocumentPreviewSection from './DocumentPreviewSection.vue'

function deepClone(value) {
    return JSON.parse(JSON.stringify(value || {}))
}

function sanitizeReviewedPayload(payload) {
    const draft = deepClone(payload)

    if (!draft.documentInformation || typeof draft.documentInformation !== 'object') {
        draft.documentInformation = {}
    }

    if (!draft.classification || typeof draft.classification !== 'object') {
        draft.classification = {}
    }

    if (!Array.isArray(draft.legislations)) {
        draft.legislations = []
    }

    if (!Array.isArray(draft.products)) {
        draft.products = []
    }
    draft.products = draft.products.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        delete row.id
        delete row.productId

        if (row.extractedProductId === undefined) {
            row.extractedProductId = null
        }

        return row
    })

    if (!draft.substanceAssessment || typeof draft.substanceAssessment !== 'object') {
        draft.substanceAssessment = {}
    }

    if (!Array.isArray(draft.substanceAssessment.substances)) {
        draft.substanceAssessment.substances = []
    }
    draft.substanceAssessment.substances = draft.substanceAssessment.substances.map(item => {
        const row = item && typeof item === 'object' ? deepClone(item) : {}

        delete row.id
        delete row.matchedSubstanceId
        delete row.substanceId

        if (row.extractedSubstanceId === undefined) {
            row.extractedSubstanceId = null
        }

        return row
    })

    if (!Array.isArray(draft.productLinks)) {
        draft.productLinks = []
    }

    if (!Array.isArray(draft.substanceLinks)) {
        draft.substanceLinks = []
    }

    return draft
}

export default {
    name: 'ReviewItemPanel',
    components: {
        BAlert,
        BCol,
        BRow,
        DocumentComplianceEditor,
        DocumentPreviewSection,
        ReviewItemActionsSection,
    },
    mixins: [backendResponse],
    props: {
        processId: {
            type: [String, Number],
            required: true,
        },
        supplierId: {
            type: [String, Number],
            default: null,
        },
        item: {
            type: Object,
            default: null,
        },
        reviewCapabilities: {
            type: Object,
            default: () => ({}),
        },
        showCancel: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: Boolean,
            default: true,
        },
        defaultStatusOnOpen: {
            type: String,
            default: null,
        },
        cancelLabel: {
            type: String,
            default() {
                return this.$t('Component(Button):cancel')
            },
        },
        saveLabel: {
            type: String,
            default() {
                return this.$t('Component(Button):save')
            },
        },
    },
    data() {
        return {
            draftReviewedPayload: {},
            status: 'pending',
            comment: '',
            saving: false,
            violations: [],
            statusOptions: [
                { value: 'pending', text: this.$t('Component(ResearchReviewProcessEnum):item-status-pending') },
                { value: 'reviewed', text: this.$t('Component(ResearchReviewProcessEnum):item-status-reviewed') },
            ],
        }
    },
    computed: {
        fileReferenceId() {
            return this.item && this.item.metadata ? this.item.metadata.fileReferenceId : null
        },
    },
    watch: {
        item: {
            immediate: true,
            handler() {
                this.bootstrap()
            },
        },
    },
    methods: {
        bootstrap() {
            this.violations = []

            let sourcePayload = {}

            if (this.item && this.item.reviewedPayload) {
                sourcePayload = this.item.reviewedPayload
            } else if (this.item && this.item.rawPayload) {
                sourcePayload = this.item.rawPayload
            }

            this.draftReviewedPayload = sanitizeReviewedPayload(sourcePayload)
            this.status = this.getInitialStatus()
            this.comment = ''
        },
        getInitialStatus() {
            const itemStatus = this.item && this.item.status ? this.item.status : 'pending'

            if (itemStatus === 'pending' && this.defaultStatusOnOpen) {
                return this.defaultStatusOnOpen
            }

            return itemStatus
        },
        save() {
            if (!this.item || !this.item.id) {
                return Promise.resolve()
            }

            this.saving = true
            this.violations = []

            return ResearchReviewApi
                .updateItem(this.processId, this.item.id, {
                    status: this.status,
                    reviewedPayload: sanitizeReviewedPayload(this.draftReviewedPayload),
                    comment: this.comment || undefined,
                })
                .then(() => ResearchReviewApi.getProcess(this.processId))
                .then(response => {
                    const process = response && response.data ? response.data : null
                    const refreshedItems = process && Array.isArray(process.items) ? process.items : []
                    const refreshedItem = refreshedItems.find(item => item.id === this.item.id)

                    if (refreshedItem) {
                        this.$emit('saved', refreshedItem)
                    } else {
                        this.$emit('saved', {
                            ...this.item,
                            status: this.status,
                            reviewedPayload: deepClone(this.draftReviewedPayload),
                        })
                    }
                })
                .catch(e => {
                    if (e && e.response && e.response.status === 422 && e.response.data && Array.isArray(e.response.data.violations)) {
                        this.violations = e.response.data.violations
                        return
                    }

                    this.handleBackendError(e)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
}
</script>

<style>
/* vue-select dropdown rendered to body must be above bootstrap modal/backdrop */
.vs__dropdown-menu {
    z-index: 2000 !important;
}

.document-compliance-preview-column {
    position: sticky;
    top: 1rem;
    align-self: flex-start;
}

@media (max-width: 767.98px) {
    .document-compliance-preview-column {
        position: static;
    }
}
</style>
